import { REGGORA_PANEL_ID, PANEL_SOURCE } from '../constants/panelSourceConstants'

/**
 * Validates if a string is a valid MongoDB ObjectId
 * ObjectIds are 24-character hexadecimal strings
 * @param {string} str - String to validate
 * @returns {boolean} True if the string is a valid ObjectId
 */
export const isObjectId = (str) => {
  if (typeof str !== 'string') {
    return false
  }
  return /^[0-9a-fA-F]{24}$/.test(str)
}

/**
 * Finds the first branch in hierarchy with panelists based on aggregation results
 * Traverses from child to parent (first to last in array) until finding a branch with panelists
 * @param {Array} branchHierarchy - Array of branch objects in reverse hierarchical order: [child, parent, grandparent, ...]
 * @param {Object} panelistCounts - Object mapping branch_id to panelist count
 * @returns {string|null} - Branch ID with panelists, or null if none found (defaults to Lender Panel)
 */
export const findDefaultBranchWithPanelists = (branchHierarchy, panelistCounts) => {
  if (!branchHierarchy || !Array.isArray(branchHierarchy)) {
    return null
  }

  for (let i = 0; i < branchHierarchy.length; i++) {
    const branch = branchHierarchy[i]
    const branchId = branch.branch_id

    // Skip null (Lender Panel), undefined, and REGGORA_PANEL_ID
    if (branchId && branchId !== REGGORA_PANEL_ID && branchId !== undefined) {
      const count = panelistCounts[branchId] || 0
      if (count > 0) {
        return branchId
      }
    }
  }

  // No branches in hierarchy have panelists, return null to default to Lender Panel
  return null
}

/**
 * Extracts branch IDs from hierarchy, filtering out invalid values
 * @param {Array} branchHierarchy - Array of branch objects
 * @returns {Array} - Array of valid branch IDs
 */
export const extractBranchIds = (branchHierarchy) => {
  if (!branchHierarchy || !Array.isArray(branchHierarchy)) {
    return []
  }

  return branchHierarchy
    .map((branch) => branch.branch_id)
    .filter((id) => id !== null && id !== REGGORA_PANEL_ID && id !== undefined)
}

/**
 * Determines panel source filter based on loan and order data
 * @param {Object} selectedLoan - Selected loan object
 * @param {Object} orderData - Order data object
 * @param {boolean} is_amc_lender - Whether user is AMC lender
 * @returns {Object} - Panel source filter object with panelSource property
 */
export const determinePanelSourceFilter = (selectedLoan, orderData, is_amc_lender) => {
  const branchId = selectedLoan?.branch?.id || selectedLoan?.branch_id
  const panelStrict = selectedLoan?.branch?.panel_strict || selectedLoan?.branch_panel_strict
  const preferredPanelId = orderData?.preferred_panel?.id
  const panelSourceFilter = {}

  if (branchId && panelStrict && !is_amc_lender) {
    panelSourceFilter.panelSource = PANEL_SOURCE.BRANCH
  } else if (preferredPanelId && is_amc_lender) {
    panelSourceFilter.panelSource = PANEL_SOURCE.PREFERRED
  }

  return panelSourceFilter
}

/**
 * Gets branch hierarchy from fullSelectedLoan or orderData
 * @param {Object} fullSelectedLoan - Full selected loan object
 * @param {Object} orderData - Order data object
 * @returns {Array|null} - Branch hierarchy array or null
 */
export const getBranchHierarchy = (fullSelectedLoan, orderData) => {
  return (
    fullSelectedLoan?.data?.branch_hierarchy ||
    orderData?.loan_file?.branch_hierarchy ||
    null
  )
}

/**
 * Checks if branch hierarchy is valid for aggregation query
 * @param {Array} branchHierarchy - Branch hierarchy array
 * @returns {boolean} - True if hierarchy is valid
 */
export const isValidBranchHierarchy = (branchHierarchy) => {
  return Boolean(branchHierarchy && Array.isArray(branchHierarchy) && branchHierarchy.length > 0)
}
